import '../scss/app.scss';

const CONFIG = {
  SCROLL_THRESHOLD: 48,
  SELECTORS: {
    navbar: '.navbar',
    navToggle: '[data-nav-toggle]',
    primaryNav: '#primary-nav',
    reveal: '[data-reveal]',
    year: '#year',
  },
};

const select = (selector: string): HTMLElement | null => document.querySelector(selector);
const selectAll = (selector: string): HTMLElement[] => [...document.querySelectorAll(selector)] as HTMLElement[];

/**
 * Stamp the current year into the footer.
 */
const updateYear = (): void => {
  const yearElement = select(CONFIG.SELECTORS.year);
  if (yearElement) {
    yearElement.textContent = new Date().getFullYear().toString();
  }
};

/**
 * Swap the navbar to its scrolled (solid/blurred) state past the threshold.
 */
const handleNavbarScroll = (): void => {
  const navbar = select(CONFIG.SELECTORS.navbar);
  if (!navbar) {
    return;
  }

  navbar.classList.toggle('nav-scroll', window.scrollY > CONFIG.SCROLL_THRESHOLD);
};

/**
 * Mobile nav toggle + auto-close on link click.
 */
const setupNavigation = (): void => {
  const navToggle = select(CONFIG.SELECTORS.navToggle);
  const primaryNav = select(CONFIG.SELECTORS.primaryNav);

  if (!navToggle || !primaryNav) {
    return;
  }

  const toggleNav = (): void => {
    const isOpen = primaryNav.classList.toggle('is-open');
    navToggle.setAttribute('aria-expanded', String(isOpen));
  };

  navToggle.addEventListener('click', toggleNav);

  selectAll(`${CONFIG.SELECTORS.primaryNav} a`).forEach((link) => {
    link.addEventListener('click', () => {
      if (primaryNav.classList.contains('is-open')) {
        toggleNav();
      }
    });
  });
};

/**
 * Fade/slide sections into view once they cross the viewport, replacing the
 * old WOW.js dependency with a small IntersectionObserver utility.
 */
const setupScrollReveal = (): void => {
  const revealTargets = selectAll(CONFIG.SELECTORS.reveal);
  if (!revealTargets.length) {
    return;
  }

  if (!('IntersectionObserver' in window)) {
    revealTargets.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
  );

  revealTargets.forEach((el) => observer.observe(el));
};

const initApp = (): void => {
  updateYear();
  setupNavigation();
  setupScrollReveal();
  handleNavbarScroll();

  window.addEventListener('scroll', handleNavbarScroll, { passive: true });
};

document.addEventListener('DOMContentLoaded', initApp);
